// Single source of truth for human-readable dates across the SPA. Under
// 24 hours from now returns the relative form ("17 min ago", "3 h ago");
// older timestamps return the European absolute form ("18.04.2026,
// 14:30"). Consumers pass either an ISO string (typical API payload) or
// a Date, null when the source has no date yet.

const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function formatAbsolute(date: Date): string {
  const day = pad(date.getDate())
  const month = pad(date.getMonth() + 1)
  const year = date.getFullYear()
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  return `${day}.${month}.${year}, ${hours}:${minutes}`
}

function formatRelative(diffMs: number): string {
  const absMs = Math.abs(diffMs)
  if (absMs < MINUTE_MS) return 'just now'
  if (absMs < HOUR_MS) return `${Math.round(absMs / MINUTE_MS)} min ago`
  return `${Math.round(absMs / HOUR_MS)} h ago`
}

export function formatDate(input: string | Date | null | undefined, now: Date = new Date()): string {
  if (!input) return ''
  const date = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(date.getTime())) return ''
  const diff = now.getTime() - date.getTime()
  return diff >= 0 && diff < DAY_MS ? formatRelative(diff) : formatAbsolute(date)
}

// Always returns the European absolute form ("18.04.2026, 14:30") so
// hover-tooltips on relative timestamps stay informative even months
// later. Empty string for null/malformed input — callers can guard the
// tooltip attribute with a v-if so the browser doesn't render a blank
// title.
export function formatAbsoluteDate(input: string | Date | null | undefined): string {
  if (!input) return ''
  const date = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(date.getTime())) return ''
  return formatAbsolute(date)
}

// Thin wrapper so callers can import a hook-shaped API if they prefer;
// `formatDate` is equally usable on its own.
export function useFormattedDate() {
  return { formatDate, formatAbsoluteDate }
}
